import React, { useCallback, useState } from 'react';
import { Modal } from './Modal';
import { ModalHeader } from './ModalHeader';
import { ModalDownloadButton } from './ModalDownloadButton';
import { ModalFilters } from './ModalFilters';
import { SuccessCriteriaLegend } from './SuccessCriteriaLegend';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { useClassInsights } from './ClassInsightsContext';
import { SECTIONS } from './export/fileNaming';
import {
  catalogClasses,
  selectedClasses,
  selectedSubjects,
  selectedTaskTypes,
  selectedTasks,
  deriveSubjectOptions,
  deriveTaskOptions,
  deriveStudentOptions,
} from './catalogSelectors';
import {
  SUCCESS_CRITERIA_SORT_OPTIONS,
  normalizeSuccessCriteriaPreviewData,
  sortSuccessCriteriaObjectives,
  successCriteriaSortOrder,
} from './utils';
import QuestionTooltip from '../../components2/QuestionTooltip';
import previewIcon from '../../static/img/preview.svg';
import SuccessCriteriaDialog from '../Shared/Dialogs/successCriteria';
import { getSuccessCriteria } from '../../service';

const MIN_SEGMENT_LABEL_PERCENT = 8;
const shouldShowSegmentLabel = (value) => value > MIN_SEGMENT_LABEL_PERCENT;
const formatOneDecimal = (value) => `${Number(value || 0).toFixed(1)}%`;
const SATISFACTION_LEVELS = [
  { key: 'satisfied', valueKey: 'satisfiedPercentage', className: 'ci-sc-satisfied', csvKey: 'Satisfied' },
  { key: 'partiallySatisfied', valueKey: 'partiallySatisfiedPercentage', className: 'ci-sc-partially', csvKey: 'PartiallySatisfied' },
  { key: 'notSatisfied', valueKey: 'notSatisfiedPercentage', className: 'ci-sc-not-satisfied', csvKey: 'NotSatisfied' },
];

const clampPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) {
    return 0;
  }
  if (num > 100) {
    return 100;
  }
  return num;
};

const getObjectiveBreakdown = (objective) => {
  const values = SATISFACTION_LEVELS.reduce((acc, level) => {
    acc[level.key] = clampPercent(objective?.[level.valueKey]);
    return acc;
  }, {});
  const total = Object.values(values).reduce((sum, value) => sum + value, 0);

  if (total === 0) {
    return { hasAssessments: false, widths: values };
  }

  if (total === 100) {
    return { hasAssessments: true, widths: values };
  }

  return { hasAssessments: true, widths: SATISFACTION_LEVELS.reduce((acc, level) => {
    acc[level.key] = (values[level.key] / total) * 100;
    return acc;
  }, {}) };
};

export function SuccessCriteriaModal({
  open,
  onClose,
  onExited,
  list,
  selectedIds,
  onChange,
  stats,
  isLoading,
  exportMode = false,
  sortBy,
  onSortChange,
}) {
  const { filters, setFilters, catalog } = useClassInsights();
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  const sortOptions = SUCCESS_CRITERIA_SORT_OPTIONS;
  const sortOrder = successCriteriaSortOrder(sortBy);

  const allItems = Array.isArray(stats) ? stats : [];
  const items = (exportMode || selectedIds.length === 0)
    ? allItems
    : allItems.filter((item) =>
        selectedIds.some(id => String(item.id) === String(id))
      );

  const csv = items.flatMap((criteria) =>
    sortSuccessCriteriaObjectives(criteria.objectives || [], sortOrder).map((objective) => ({
      Criteria: criteria.title,
      Objective: objective.title,
      ...SATISFACTION_LEVELS.reduce((acc, level) => {
        acc[level.csvKey] = formatOneDecimal(objective[level.valueKey]);
        return acc;
      }, {}),
    }))
  );

  // Build filter data for export
  const classes = catalogClasses(catalog);
  const classLabels = filters.classIds
    .map(id => classes.find(c => String(c.id) === String(id)))
    .filter(Boolean)
    .map(c => c.title || c.name);

  const successCriteriaLabels = selectedIds
    .map(id => list.find(sc => String(sc.id) === String(id)))
    .filter(Boolean)
    .map(sc => sc.title || sc.name || 'Untitled Success Criteria');

  // Get options for other filters
  const selectedClassesData = selectedClasses(catalog, filters);
  const selectedSubjectsData = selectedSubjects(selectedClassesData, filters);
  const selectedTaskTypesData = selectedTaskTypes(selectedSubjectsData, filters);
  const selectedTasksData = selectedTasks(selectedTaskTypesData, filters);

  const subjectOptions = deriveSubjectOptions(selectedClassesData);
  const taskOptions = deriveTaskOptions(selectedTaskTypesData);
  const studentOptions = deriveStudentOptions(selectedClassesData);

  // Build filter labels
  const subjectLabels = filters.subjectCodes
    ?.map(code => subjectOptions.find(s => s.id === code)?.label)
    .filter(Boolean) || [];

  const taskTypeLabels = filters.taskTypes || [];

  const taskNameLabels = filters.taskIds
    ?.map(id => taskOptions.find(t => String(t.id) === String(id))?.label)
    .filter(Boolean) || [];

  const studentLabels = filters.studentIds
    ?.map(id => studentOptions.find(s => String(s.id) === String(id))?.label)
    .filter(Boolean) || [];

  // Build date range string
  const dateRange = filters.startDate && filters.endDate
    ? `${filters.startDate} to ${filters.endDate}`
    : null;

  const exportFilters = {
    classLabels,
    successCriteriaLabels,
    dateRange,
    filters: {
      subjects: subjectLabels,
      taskTypes: taskTypeLabels,
      taskIds: taskNameLabels,
      studentIds: studentLabels,
    },
  };

  const openSuccessCriteriaPreview = useCallback(async (criteria) => {
    const criteriaId = criteria?.id;
    if (!criteriaId) return;
    try {
      const details = await getSuccessCriteria(criteriaId);
      setPreviewData(normalizeSuccessCriteriaPreviewData(details, criteria));
    } catch (error) {
      setPreviewData(normalizeSuccessCriteriaPreviewData(null, criteria));
    }
    setShowPreviewDialog(true);
  }, []);

  return (
    <Modal
      open={open}
      onExited={onExited}
      onOverlayClick={onClose}
      className="ci-modal-wide ci-success-criteria-modal"
      data-section="success-criteria-modal"
    >
      <ModalHeader title="Success Criteria" onClose={onClose}>
        <ExportFormatDropdown
          section={SECTIONS.SUCCESS_CRITERIA}
          data={items}
          csvData={csv}
          isModal={true}
          filterData={exportFilters}
          trigger={<ModalDownloadButton />}
        />
      </ModalHeader>

      <ModalFilters
        firstFilterLabel="Success criteria"
        firstFilterOptions={list}
        firstFilterValue={selectedIds}
        firstFilterOnChange={onChange}
        firstFilterGetOptionLabel={(opt) => opt.title}
        firstFilterGetOptionValue={(opt) => String(opt.id)}
        firstFilterShowAllOption={true}
        filters={filters}
        setFilters={setFilters}
        className="ci-comment-bank-modal-filters"
      />

      <SuccessCriteriaLegend variant="modal" />

      {!isLoading && items.length > 0 && sortBy && onSortChange && (
        <div className="ci-modal-table-controls">
          <div className="ci-modal-sort">
            <span>Sort by:</span>
            <DropdownMenu
              multiselect
              singleSelect
              options={sortOptions}
              selectedValues={[sortBy]}
              onChange={(values) => onSortChange(values[0] || sortOptions[0])}
              placeholder="Sort"
              getOptionLabel={(opt) => opt}
              getOptionValue={(opt) => opt}
              alwaysWhite
              compactDisplay
              showCheckboxes={false}
              showAllOption={false}
              triggerFontSize="14px"
              triggerPadding="8px 12px"
              triggerWidth="auto"
              triggerMinWidth="280px"
            />
          </div>
        </div>
      )}

      <div className="ci-sc-content">
        {isLoading ? (
          <div className="ci-panel-loading">
            <SmallLoader />
          </div>
        ) : items.length === 0 ? (
          <div className="ci-empty-state">No data available</div>
        ) : (
          items.map((section) => (
            <div key={section.id} className="ci-sc-section">
              <h4 className="ci-sc-section-title">
                <span>{section.title}</span>
                <button
                  type="button"
                  className="ci-criteria-preview-button"
                  onClick={() => openSuccessCriteriaPreview(section)}
                  aria-label={`View marking criteria for ${section.title || 'selected success criteria'}`}
                >
                  <img src={previewIcon} alt="" width={18} height={18} />
                </button>
              </h4>
              <div className="ci-sc-grid">
                {(section.objectives || []).length === 0 ? (
                  <div className="ci-sc-no-assessments">No assessments yet</div>
                ) : sortSuccessCriteriaObjectives(section.objectives || [], sortOrder).map((item, itemIndex) => {
                  const breakdown = getObjectiveBreakdown(item);
                  return (
                    <div key={`${item.title}-${itemIndex}`} className="ci-sc-card">
                      <div className="ci-sc-card-title">{item.title}</div>
                      {breakdown.hasAssessments ? (
                        <QuestionTooltip
                          text={`Satisfied: ${formatOneDecimal(item.satisfiedPercentage)} | Partially Satisfied: ${formatOneDecimal(item.partiallySatisfiedPercentage)} | Not Satisfied: ${formatOneDecimal(item.notSatisfiedPercentage)}`}
                        >
                          <div className="ci-sc-bar">
                            {SATISFACTION_LEVELS.map((level) => (
                              <div
                                key={level.key}
                                className={`ci-sc-segment ${level.className}`}
                                style={{ width: `${breakdown.widths[level.key]}%` }}
                              >
                                {shouldShowSegmentLabel(breakdown.widths[level.key]) ? formatOneDecimal(item[level.valueKey]) : ''}
                              </div>
                            ))}
                          </div>
                        </QuestionTooltip>
                      ) : (
                        <div className="ci-sc-no-assessments">No assessments yet</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
      {showPreviewDialog && previewData && (
        <SuccessCriteriaDialog
          successCriteria={previewData}
          setSuccessCriteriaPreviewDialog={setShowPreviewDialog}
          showEditButton={false}
        />
      )}
    </Modal>
  );
}
